import { wizardOrbAtScreenPoint } from "./wizard-orbs.js";
import { enemyByKind } from "./enemy-defs.js";
/** Left-click picking for the floor. Keep the screen point until after the
 * visible NPC body has been tested: snapping to its foot tile first loses the
 * distinction between a sprite click and a ground click on that same tile. */

import type { Observation, ObservedPlayer } from "@pd/shared";
import { attackLabel, enemyNear, groundAction, objectsAt, primaryAction, wizardOrbAt } from "./actions.js";
import type { PrimaryAction, Vec2, WorldObject } from "./actions.js";
import { doorTileAtScreenPoint, enemyHitsAtScreenPoint, groundObjectTileAtScreenPoint } from "./hitbox.js";
import type { HeroAssets } from "./hero.js";
import type { ScreenPoint } from "./viewport.js";

export function floorPointAction(
  assets: HeroAssets | null,
  obs: Observation,
  me: ObservedPlayer,
  point: ScreenPoint,
  positions: ReadonlyMap<string, Vec2>,
): PrimaryAction {
  const orb = wizardOrbForPoint(assets, obs, point, positions);
  if (orb) return { kind: "interact", label: "Burst orb", obj: orb };
  const enemy = enemyHitsAtScreenPoint(
    assets, obs, positions, point.sx, point.sy, point.isoUnit, point.camX, point.camY,
  )[0];
  if (enemy) {
    const name = enemyByKind(enemy.kind)?.name ?? "enemy";
    return {
      kind: "attack",
      label: attackLabel(name, enemy, obs.combat).label,
      target: enemy.id,
      x: enemy.x,
      y: enemy.y,
      obj: { kind: "enemy", id: enemy.id, x: enemy.x, y: enemy.y },
    };
  }

  // An exact ground tile beats the small object margin. Both are ground
  // intents, including when an item shares the NPC's foot tile.
  const groundTile = objectsAt(obs, point.tile.x, point.tile.y).length > 0
    ? point.tile
    : groundObjectTileAtScreenPoint(obs, point.sx, point.sy, point.isoUnit, point.camX, point.camY);
  if (groundTile) return primaryAction(obs, me, groundTile.x, groundTile.y);

  const doorTile = doorTileAtScreenPoint(assets, obs, point.sx, point.sy, point.isoUnit, point.camX, point.camY);
  if (doorTile) return primaryAction(obs, me, doorTile.x, doorTile.y);

  if (enemyNear(obs, point.tile.x, point.tile.y)) {
    return groundAction(obs, me, point.tile.x, point.tile.y);
  }
  return primaryAction(obs, me, point.tile.x, point.tile.y);
}

/** Resolve the orb first across its sprite, floor tile, and covered objects. */
export function wizardOrbForPoint(
  assets: HeroAssets | null,
  obs: Observation,
  point: ScreenPoint,
  positions: ReadonlyMap<string, Vec2>,
): Extract<WorldObject, { kind: "wizardOrb" }> | null {
  const direct = wizardOrbAtScreenPoint(obs, point.sx, point.sy, point.isoUnit, point.camX, point.camY);
  if (direct) return direct;

  // NPC billboards rise above their floor diamonds. If one is on an orb's
  // tile, the whole visible NPC body belongs to that orb while it is active.
  const enemy = enemyHitsAtScreenPoint(assets, obs, positions, point.sx, point.sy, point.isoUnit, point.camX, point.camY)
    .find((hit) => wizardOrbAt(obs, hit.x, hit.y));
  if (enemy) return wizardOrbAt(obs, enemy.x, enemy.y);

  // Drops can be fanned away from their anchor tile. Preserve the orb's input
  // priority when the click is resolved through the visible ground object.
  const groundTile = objectsAt(obs, point.tile.x, point.tile.y).length > 0
    ? point.tile
    : groundObjectTileAtScreenPoint(obs, point.sx, point.sy, point.isoUnit, point.camX, point.camY);
  return groundTile ? wizardOrbAt(obs, groundTile.x, groundTile.y) : null;
}
